// ===== ProductInfo Component =====
const ProductInfo = class extends HTMLElement {
  constructor() {
    super();
    this.abortController = null;
    this.productCache = new Map();
    this.pendingPrefetch = new Set();
  }

  connectedCallback() {
    this.setupEventListeners();
    const removeLoadingState = () => {
      this.classList.remove('is-loading');
    };

    const variantPicker = this.querySelector('variant-picker');
    if (!variantPicker) {
      setTimeout(removeLoadingState, 100);
    }
  }

  setupEventListeners() {
    const combineProducts = this.querySelector('.combine-products');
    if (combineProducts) {
      combineProducts.addEventListener('click', this.onCombineProductClick.bind(this));

      const warm = (event) => {
        const link = event.target.closest?.('a[href*="/products/"]');
        if (link) this.prefetchProduct(new URL(link.href).pathname);
      };

      combineProducts.addEventListener('pointerover', warm);
      combineProducts.addEventListener('focusin', warm);
      combineProducts.addEventListener('touchstart', warm, { passive: true });
    }
  }

  initVariantSelectionGuard() {
    const inputs = this.querySelectorAll('variant-picker input[type="radio"]');
    const buyButton = this.querySelector('[data-block-type="quantity-selector"] buy-buttons button');
    const buttonMsg = this.querySelector('.pre_selected_var_msg');

    if (!inputs.length || !buyButton || !buttonMsg) return;

    const isAllSelected = () => {
      const groups = new Set([...inputs].map((i) => i.name));
      return [...groups].every((name) => this.querySelector(`input[name="${name}"]:checked`));
    };

    const hide = () => {
      buttonMsg.classList.remove('visible');
      buyButton.removeAttribute('disabled');
    };

    const show = () => {
      if (!isAllSelected()) {
        buttonMsg.classList.add('visible');
        buyButton.setAttribute('disabled', 'disabled');
      }
    };

    buyButton.addEventListener('mouseenter', show);
    buyButton.addEventListener('mouseleave', hide);

    inputs.forEach((input) => input.addEventListener('change', () => (isAllSelected() ? hide() : show())));

    if (window.innerWidth <= 1000 && !isAllSelected()) {
      show();
    }
  }

  startLoading() {
    this.classList.add('is-loading');
  }

  endLoading() {
    const variantPicker = this.querySelector('variant-picker');
    if (!variantPicker) {
      this.classList.remove('is-loading');
    }
  }

  // Sends the reader back to the top of the page after a print switch.
  //
  // Every screen size, on purpose. An earlier version kept the swatches under the reader's cursor
  // by measuring where they sat and scrolling to match. That cannot work: replacing the subtree
  // changes the height of the content above it, so holding the swatches still means scrolling the
  // reader to follow them. On a desktop the gallery and the product info are two columns of one
  // grid row with .product-info sticky inside it, and a different print made the gallery 725px
  // shorter, which turned into 682px of scroll in a single frame.
  //
  // Switching prints is closer to opening a different product than to changing a size, so the top
  // of the page is where it should land. Marketing asked for the same on a phone once they had
  // seen it.
  //
  // Called twice, and that is safe here. The old code corrected twice because it was chasing a
  // layout that kept moving, and each correction was measured against the last. The top of the page
  // is a fixed target, so calling it again changes nothing.
  //
  // The first call is on the click. The second is after the swap. Without the first, a print that
  // is not already in the cache has to wait for the network, and a reader who starts scrolling
  // during that wait gets pulled back to the top when the response lands. Going up on the click
  // also answers the click straight away.
  scrollToTop() {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }

  // Fetches one sibling, when the reader shows intent. Fetching all of them on page load cost
  // about 173 KB and 130 ms of render each. At 66 prints that is about 11 MB, and it blocks the
  // browser connections for about 7 seconds. It also grows with every new print.
  //
  // Checks the requests in flight as well as the cache, because pointerover repeats.
  prefetchProduct(productPath) {
    if (!productPath) return;
    if (this.productCache.has(productPath) || this.pendingPrefetch.has(productPath)) return;

    const sectionId = this.closest('.shopify-section')?.id?.replace('shopify-section-', '');
    if (!sectionId) return;

    this.pendingPrefetch.add(productPath);

    this.fetchProductData(productPath, sectionId)
      .then((data) => {
        if (data) this.productCache.set(productPath, data);
      })
      .catch((err) => console.error(`Error pre-fetching product ${productPath}:`, err))
      .finally(() => this.pendingPrefetch.delete(productPath));
  }

  async fetchProductData(productPath, sectionId) {
    const url = new URL(productPath, window.location.origin);
    url.searchParams.set('sections', sectionId);

    const response = await fetch(url.toString());
    const data = await response.json();
    return data[sectionId];
  }

  onCombineProductClick(event) {
    const link = event.target.closest('a[href*="/products/"]');
    if (link && link.href) {
      event.preventDefault();
      const productUrl = new URL(link.href);
      this.renderProductSection(productUrl.pathname);
    }
  }

  async renderProductSection(productPath) {
    this.startLoading();
    this.scrollToTop();

    if (this.abortController) this.abortController.abort();
    this.abortController = new AbortController();

    try {
      const sectionId = this.closest('.shopify-section')?.id?.replace('shopify-section-', '');
      if (!sectionId) return;

      const selectedOptions = this.getSelectedOptions();

      let sectionHTML;
      if (this.productCache.has(productPath)) {
        sectionHTML = this.productCache.get(productPath);
      } else {
        sectionHTML = await this.fetchProductData(productPath, sectionId);
        if (sectionHTML) this.productCache.set(productPath, sectionHTML);
      }

      if (!sectionHTML) {
        console.warn('No section HTML returned from API');
        return;
      }

      const parser = new DOMParser();
      const doc = parser.parseFromString(sectionHTML, 'text/html');
      const newProductInfo = doc.querySelector('product-info');

      if (newProductInfo) {
        this.innerHTML = newProductInfo.innerHTML;
        // The swap replaces every child, so anything holding state on this markup has to run
        // again. The sticky add-to-cart bar is one.
        this.dispatchEvent(new CustomEvent('product-info:rendered', { bubbles: true }));
        // Again after the swap. The new markup can be taller or shorter than the old, and the
        // browser can restore a scroll position of its own when the subtree is replaced.
        this.scrollToTop();

        await this.restoreSelectedOptions(selectedOptions);

        let images = [];
        try {
          images = newProductInfo.dataset.images ? JSON.parse(newProductInfo.dataset.images) : [];
        } catch (e) {
          console.warn('Failed to parse product images JSON:', newProductInfo.dataset.images, e);
          images = [];
        }

        const productData = {
          productID: newProductInfo.dataset.productId,
          title: newProductInfo.dataset.productTitle,
          sku: newProductInfo.dataset.sku,
          tags: (newProductInfo.dataset.tags || '').split(','),
          brand: newProductInfo.dataset.brand,
          images: images,
        };

        // Initialize Kiwi Size Chart
        initKiwiSizing(productData);

        // Update browser URL
        const url = new URL(productPath, window.location.origin);
        window.history.replaceState({ path: url.pathname }, '', url.pathname);

        this.setupEventListeners();
        this.initVariantSelectionGuard();
      }

      this.dispatchEvent(
        new CustomEvent('product:changed', {
          bubbles: true,
          detail: { productPath, sectionId, selectedOptions },
        }),
      );
    } catch (error) {
      console.error('Error rendering product section:', error);
    } finally {
      setTimeout(() => {
        this.endLoading();
      }, 500);
    }
  }

  getSelectedOptions() {
    const selectedOptions = {};
    const variantPicker = this.querySelector('variant-picker');
    if (!variantPicker) return selectedOptions;
    const optionInputs = variantPicker.querySelectorAll('input[name^="option"]');

    optionInputs.forEach((input) => {
      if (input.type === 'radio' && input.checked) {
        selectedOptions[input.name] = input.value;
      }
    });
    return selectedOptions;
  }

  async restoreSelectedOptions(selectedOptions) {
    if (!selectedOptions || Object.keys(selectedOptions).length === 0) return;

    const variantPicker = this.querySelector('variant-picker');
    if (!variantPicker) return;

    // Wait for variant picker to load product data
    while (!variantPicker.product) {
      await new Promise((resolve) => setTimeout(resolve, 50));
    }

    let lastInput = null;

    Object.entries(selectedOptions).forEach(([optionName, optionValue]) => {
      const radioInput = variantPicker.querySelector(`input[name="${optionName}"][value="${optionValue}"]`);
      if (radioInput && radioInput.type === 'radio') {
        radioInput.checked = true;
        lastInput = radioInput;
      }
    });

    if (lastInput) {
      lastInput.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }
};

if (!customElements.get('product-info')) customElements.define('product-info', ProductInfo);

// ===== AppsSection Component =====
const AppsSection = class extends HTMLElement {
  connectedCallback() {
    this.renderAppsSection();
  }

  renderAppsSection() {
    document.addEventListener('product:changed', async (event) => {
      const productPath = event.detail.productPath;
      const thisSectionId = this.closest('.shopify-section')?.id?.replace('shopify-section-', '');
      if (!thisSectionId) return;

      const url = new URL(productPath, window.location.origin);
      url.searchParams.set('sections', thisSectionId);

      try {
        const response = await fetch(url.toString());
        const data = await response.json();
        const sectionHTML = data[thisSectionId];

        if (sectionHTML) {
          const parser = new DOMParser();
          const doc = parser.parseFromString(sectionHTML, 'text/html');
          const newContent = doc.querySelector('apps-section');

          if (newContent) {
            this.innerHTML = newContent.innerHTML;

            // Re-init Yotpo widgets after AJAX load
            setTimeout(() => {
              if (document.querySelectorAll('.yotpo-widget-instance').length > 0) {
                document.dispatchEvent(new Event('DOMContentLoaded', { bubbles: true }));
              }
            }, 100);
          }
        }

        if (typeof yotpoWidgetsContainer !== 'undefined') yotpoWidgetsContainer.initWidgets();
      } catch (error) {
        console.error('Error updating apps section:', error);
      }
    });
  }
};

if (!customElements.get('apps-section')) customElements.define('apps-section', AppsSection);

// ===== Kiwi Size Chart Initialization =====
function initKiwiSizing(productData) {
  const cleanUpOldContainers = () => {
    const oldContainers = document.querySelectorAll('.ks-chart-container, .ks-calculator-container');

    if (oldContainers.length > 0) {
      console.log(`Found ${oldContainers.length} old Kiwi container(s), removing them...`);
      oldContainers.forEach((el) => el.remove());
    } else {
      console.log('No old Kiwi containers found.');
    }
  };

  const loadKiwi = () => {
    // Clean previous containers first
    cleanUpOldContainers();

    // Load new Kiwi sizing
    ks.loadSizing({ productData, options: {} });
  };

  if (!window.ks) {
    const retryInterval = setInterval(() => {
      if (window.ks) {
        clearInterval(retryInterval);
        loadKiwi();
      }
    }, 100);
    return;
  }

  loadKiwi();
}

document.addEventListener('variant:change', function (e) {
  const variant = e.detail.variant;
  if (!variant) return;

  const buyButton = document.querySelector(".buy-buttons button[type='submit']");
  const souldOutLabel = document.querySelector('[data-sould-out]');

  if (buyButton) {
    buyButton.innerText = variant.available ? 'ADD TO CART' : 'SOLD OUT';
    buyButton.disabled = !variant.available;
  }

  if (souldOutLabel) {
    variant.available ? souldOutLabel.classList.add('hidden') : souldOutLabel.classList.remove('hidden');
  }
});
